import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileCategoryOpen(false);
  };

  const handleGoToMain = () => {
    window.open('https://kaspreadsheet.com', '_blank', 'noopener,nofollow');
  };

  const handleCategoryClick = (url: string) => {
    closeMobile();
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70' : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <a href="/" className="font-heading text-xl text-foreground-950 whitespace-nowrap">
            Sugargoo Sheet
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-5">
            <a href="/" className="text-sm text-foreground-700 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
              Home
            </a>
            <a href="/#guides" className="text-sm text-foreground-700 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">
              Shopping Guide
            </a>
            <button
              onClick={handleGoToMain}
              className="text-sm bg-primary-500 text-background-50 px-5 py-2 rounded-md hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </button>
          </nav>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-700"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" ref={menuRef}>
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeMobile}
          ></div>
          <div className="absolute top-16 right-0 w-full max-w-sm bg-background-50 h-[calc(100vh-4rem)] overflow-y-auto shadow-lg">
            <div className="p-5 space-y-1">
              <a
                href="/"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
                  <i className="ri-home-4-line"></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">Home</span>
              </a>

              <a
                href="/sugargoo-spreadsheet-guide"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
                  <i className="ri-book-open-line"></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">Complete Guide</span>
              </a>

              <a
                href="/#guides"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
                  <i className="ri-compass-3-line"></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">Shopping Guide</span>
              </a>

              <button
                onClick={() => {
                  closeMobile();
                  handleGoToMain();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-shopping-cart-2-line"></i>
                </span>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">Buy It Now</span>
              </button>
            </div>

            {/* Categories in Mobile */}
            <div className="px-5 pt-4 pb-2">
              <button
                onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-100/60 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100/70 text-accent-600">
                    <i className="ri-grid-line"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">Browse Categories</span>
                </span>
                <span className="w-5 h-5 flex items-center justify-center">
                  {mobileCategoryOpen ? (
                    <i className="ri-arrow-up-s-line text-foreground-400"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-foreground-400"></i>
                  )}
                </span>
              </button>

              {mobileCategoryOpen && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategoryClick(cat.url)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer text-left"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600 flex-shrink-0">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      <span className="text-xs text-foreground-700 font-medium whitespace-nowrap truncate">
                        {cat.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Article Links in Mobile */}
            <div className="px-5 pt-2 pb-5">
              <p className="text-xs text-foreground-500 font-medium px-4 py-2 uppercase tracking-wider">SEO Guides</p>
              <div className="space-y-0.5">
                {[
                  { label: 'Beginner Guide', href: '/sugargoo-spreadsheet-guide' },
                  { label: 'Spreadsheet Review', href: '/sugargoo-spreadsheet-review' },
                  { label: 'Shipping Cost Guide', href: '/sugargoo-shipping-cost' },
                  { label: 'Coupon & Discounts', href: '/sugargoo-coupon-guide' },
                  { label: 'Best Finds 2026', href: '/best-sugargoo-finds' },
                  { label: 'Is Sugargoo Legit?', href: '/is-sugargoo-legit' },
                  { label: 'Sugargoo Alternatives', href: '/sugargoo-alternatives' },
                  { label: 'Shoes Guide', href: '/sugargoo-shoes-guide' },
                  { label: 'Fashion Guide', href: '/sugargoo-fashion-guide' },
                  { label: 'Money Saving Tips', href: '/sugargoo-saving-tips' },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={closeMobile}
                    className="block px-4 py-2 text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100/60 rounded-lg transition-colors duration-200 whitespace-nowrap"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}